// src/components/booking/BookingTemplate.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Grid, 
  List,
  Star, 
  MapPin, 
  Heart,
  Share2,
  Calendar,
  Users,
  Building2,
  Bed,
  Car,
  Clock,
  Wifi,
  Coffee,
  ParkingCircle,
  UtensilsCrossed,
  Send,
  Bell,
  Settings,
  CheckCircle,
  X
} from 'lucide-react';
import { bookingService } from '../../services/bookingService';
import { firestoreService } from '../../services/firestoreService';
import { AccommodationPlaceCard } from '../booking/PlaceCardView';
import { AccommodationPlaceRow } from '../booking/PlaceListView';
import { SendRequest } from '../booking/SendRequest';
import { BookingHeroSection } from './sections/HeroSection';
import SharedHeader from '../shared/SharedHeader';
import { getHeaderConfig } from '../configs/headerConfigs';

const ACCOMMODATION_TYPES = [
  { value: 'hotel', label: 'Hotels', icon: Building2 },
  { value: 'bnb', label: 'Bed & Breakfast', icon: Bed },
  { value: 'guesthouse', label: 'Guesthouses', icon: Building2 },
  { value: 'lodge', label: 'Lodges', icon: Building2 },
  { value: 'resort', label: 'Resorts', icon: Building2 },
  { value: 'apartment', label: 'Apartments', icon: Building2 },
  { value: 'villa', label: 'Villas', icon: Building2 },
  { value: 'cottage', label: 'Cottages', icon: Building2 },
  { value: 'farm', label: 'Farm Stays', icon: Building2 },
  { value: 'camping', label: 'Camping', icon: Building2 }
];

export const BookingTemplate = ({ 
  user,
  bookId, 
  bookData, 
  customStyling, 
  onBookingRequest 
}) => {
  const [activeTab, setActiveTab] = useState('search');
  const navigate = useNavigate();
  const [searchCriteria, setSearchCriteria] = useState({
    checkIn: '',
    checkOut: '',
    adults: 2,
    children: 0,
    rooms: 1,
    location: '',
    province: '',
    city: ''
  });
  
  // Broadcast request data
  const [broadcastRequest, setBroadcastRequest] = useState({
    location: '',
    province: '',
    city: '',
    checkIn: '',
    checkOut: '',
    adults: 2,
    children: 0,
    rooms: 1,
    accommodationType: [],
    budget: { min: 0, max: 5000 },
    amenities: [],
    message: ''
  });

  // Filters - empty by default, only apply when values are set
  const [filters, setFilters] = useState({
    accommodationType: [],
    priceRange: [0, 10000], 
    rating: 0,
    amenities: [],
    starRating: 0,
    guestRating: 0,
    location: '',
    searchQuery: ''
  });

  // All listings (unfiltered)
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestSentStatus, setRequestSentStatus] = useState(null);
  
  const isOwner = user?.uid === bookData?.ownerId;
  const bookingSettings = bookData?.bookingSettings || {};
  const isMultiVendor = bookingSettings.businessType === 'multi';

  const headerConfig = getHeaderConfig('album', {
    // Custom album-specific config
    showDescription: true
  });

  // Load all listings once when component mounts
  useEffect(() => {
    loadAllListings();
  }, [bookId]);

  const loadAllListings = async () => {
    if (!bookId) return;
    
    setLoading(true);
    try {
      // Get ALL listings without any filters
      const data = await bookingService.getBookingListings(bookId);
      console.log('Loaded listings:', data); // Debug log
      setAllListings(data || []);
    } catch (error) {
      console.error('Error loading listings:', error);
      setAllListings([]);
    } finally {
      setLoading(false);
    }
  };

  const availableLocations = useMemo(() => {
    
    const locationMap = new Map();
    
    allListings.forEach(listing => {
      if (listing.status === 'active' && listing.location) {
        const location = listing.location.trim();
        
        let city = '';
        let province = '';
        
        if (location.includes(',')) {
          const parts = location.split(',').map(part => part.trim());
          if (parts.length >= 2) {
            city = parts[0];
            province = parts[1];
          } else {
            province = parts[0];
          }
        } else {
          province = location;
        }
        
        const key = province.toLowerCase();
        
        if (!locationMap.has(key)) {
          locationMap.set(key, {
            province: province,
            cities: new Set()
          });
        }
        
        if (city) {
          locationMap.get(key).cities.add(city);
        }
      }
    });
    
    // Convert to format matching SA_LOCATIONS
    const locations = {};
    locationMap.forEach((value, key) => {
      locations[value.province] = Array.from(value.cities).sort();
    });
    
    return locations;
  }, [allListings]);

  const formattedLocations = useMemo(() => {
    const formatted = [];
    
    Object.entries(availableLocations).forEach(([province, cities]) => {
      // Add province-only option
      formatted.push({
        value: province,
        label: province,
        type: 'province'
      });
      
      // Add city, province combinations
      cities.forEach(city => {
        formatted.push({
          value: `${city}, ${province}`,
          label: `${city}, ${province}`,
          type: 'city'
        });
      });
    });
    
    return formatted.sort((a, b) => {
      // Sort provinces first, then cities within provinces
      if (a.type !== b.type) {
        return a.type === 'province' ? -1 : 1;
      }
      return a.label.localeCompare(b.label);
    });
  }, [availableLocations]);

  // Function to check if any filters are actually applied
  const hasActiveFilters = () => {
    return (
      filters.searchQuery?.trim() || 
      filters.location?.trim() ||
      filters.accommodationType.length > 0 ||
      filters.priceRange[0] > 0 || filters.priceRange[1] < 10000 ||
      filters.starRating > 0 ||
      filters.guestRating > 0 ||
      filters.amenities.length > 0
    );
  };

  // Filter listings only when filters are actually applied
  const getFilteredListings = () => {
    // If no active filters, return all listings
    if (!hasActiveFilters()) {
      return allListings.filter(listing => listing.status === 'active');
    }

    return allListings.filter(listing => {
      if (listing.status !== 'active') return false;

      // Search query filter
      if (filters.searchQuery?.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch = (
          listing.title?.toLowerCase().includes(query) ||
          listing.description?.toLowerCase().includes(query) ||
          listing.location?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // Location filter
      if (filters.location?.trim()) {
        const location = filters.location.toLowerCase();
        if (!listing.location?.toLowerCase().includes(location)) return false;
      }

      // Price range filter (only if range is narrowed from default)
      if (filters.priceRange[0] > 0 || filters.priceRange[1] < 10000) {
        const price = listing.price || 0;
        if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;
      }

      // Accommodation type filter
      if (filters.accommodationType.length > 0) {
        if (!filters.accommodationType.includes(listing.accommodationType)) return false;
      }

      // Star rating filter
      if (filters.starRating > 0) {
        const starRating = listing.starRating || 0;
        if (starRating < filters.starRating) return false;
      }

      // Guest rating filter
      if (filters.guestRating > 0) {
        const guestRating = listing.rating || listing.guestRating || 0;
        if (guestRating < filters.guestRating) return false;
      }

      // Amenities filter
      if (filters.amenities.length > 0) {
        const listingAmenities = listing.amenities || [];
        if (!filters.amenities.some(amenity => listingAmenities.includes(amenity))) return false;
      }

      return true;
    });
  };

  const filteredListings = getFilteredListings();

  // Handle date changes
  const handleDateChange = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (activeTab === 'request') {
      setBroadcastRequest(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle location selection
  const handleLocationChange = (province, city = '') => {
    const location = city ? `${city}, ${province}` : province;
    
    setSearchCriteria(prev => ({
      ...prev,
      location,
      province,
      city
    }));
    
    if (activeTab === 'request') {
      setBroadcastRequest(prev => ({
        ...prev,
        location,
        province,
        city
      }));
    }
  };

  // Clear all filters
  const clearAllSearch = () => {
    // Clear search criteria
    setSearchCriteria({
      checkIn: '',
      checkOut: '',
      adults: 2,
      children: 0,
      rooms: 1,
      location: '',
      province: '',
      city: '',
      specificPlace: '' // if you have this field
    });
    
    // Clear filters
    setFilters({
      accommodationType: [],
      priceRange: [0, 10000],
      rating: 0,
      amenities: [],
      starRating: 0,
      guestRating: 0,
      location: '',
      searchQuery: ''
    });
    
    // Close filters panel if open
    setShowFilters(false);
  };

  // Send broadcast request to accommodation providers
  const handleSendBroadcastRequest = async () => {
    if (!broadcastRequest.location || !broadcastRequest.checkIn || !broadcastRequest.checkOut) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ...broadcastRequest,
        bookId,
        requestType: 'accommodation_broadcast',
        status: 'pending',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      };

      await firestoreService.create('pb-booking-requests', requestData);
      await bookingService.notifyProvidersInArea(broadcastRequest.location, requestData);
      
      setRequestSent(true);
      
      setTimeout(() => {
        setRequestSent(false);
        setBroadcastRequest({
          location: '',
          province: '',
          city: '',
          checkIn: '',
          checkOut: '',
          adults: 2,
          children: 0,
          rooms: 1,
          accommodationType: [],
          budget: { min: 0, max: 5000 },
          amenities: [],
          message: ''
        });
      }, 3000);

    } catch (error) {
      console.error('Error sending broadcast request:', error);
      alert('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stableBookSettings = useMemo(() => ({
    bookId,
    ...bookData
  }), [bookId, bookData]);

  const stableHandleRequestSent = useCallback(async (requestData) => {
    console.log('Request sent:', requestData);

    setRequestSentStatus('success');
    
    if (onBookingRequest) {
      onBookingRequest(requestData);
    }
  }, [bookId, bookingSettings?.bookingType, onBookingRequest]);

  const handleNavigateToSearch = (searchCriteriaFromRequest) => {
    // Update search criteria with data from the request
    setSearchCriteria(searchCriteriaFromRequest);
    
    // Switch to search tab
    setActiveTab('search');
    
    // Optionally show a message about browsing while waiting
    // You could add a toast notification here
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ customStyling }}>
      {/* Hero Section */}
      <BookingHeroSection
        bookData={bookData}
        bookingSettings={bookingSettings}
        customStyling={customStyling}
        bookId={bookId}
        onCTAClick={() => {
          // Scroll to content or perform action
          document.getElementById('content-section')?.scrollIntoView({ 
            behavior: 'smooth' 
          });
        }}
      />

      {/* Search Tabs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex border-b">
            {[
              { id: 'search', label: 'Search Places', icon: Search },
              { id: 'request', label: 'Send Request', icon: Send },
              { id: 'specific', label: 'Specific Place', icon: MapPin }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {activeTab === 'search' && (
            <SearchPlacesForm
              allListings = {allListings}
              searchCriteria={searchCriteria}
              setSearchCriteria={setSearchCriteria}
              filters={filters}
              setFilters={setFilters}
              onLocationChange={handleLocationChange}
              onDateChange={handleDateChange}
              customStyling={customStyling}
            />
          )}

          {activeTab === 'request' && (
            <SendRequest
              bookId={bookId}
              bookingType={bookData?.bookingType || 'accommodation'}
              bookSettings={stableBookSettings}       
              customStyling={customStyling}           
              onRequestSent={stableHandleRequestSent} 
              availableLocations={availableLocations}
              onNavigateToSearch={handleNavigateToSearch}
            />
          )}

          {activeTab === 'specific' && (
            <SpecificPlaceForm
              searchCriteria={searchCriteria}
              setSearchCriteria={setSearchCriteria}
              customStyling={customStyling}
              onSearch={(query) => {
                // Add specific place search to filters
                setFilters(prev => ({ ...prev, searchQuery: query }));
                setActiveTab('search'); // Switch to search tab to show results
              }}
            />
          )}
        </div>

        {/* Control Buttons */}
            <div className="fixed bottom-12 right-6 flex flex-col gap-3 z-50">
      
              {isOwner && (
                <button
                  onClick={() => navigate(`/books/${bookId}/edit`)}
                  className="p-3 rounded-full shadow-lg hover:shadow-xl transition-all text-white"
                  style={{ backgroundColor: customStyling.primaryColor }}
                  title="Edit flipbook"
                >
                  <Settings className="w-6 h-6" />
                </button>
              )}
            </div>
      </div>

      {/* Results Section - Only show for search tab */}
        {activeTab === 'search' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                              showFilters || hasActiveFilters()
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-600' 
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <Filter className="h-4 w-4" />
                            Filters
                            {hasActiveFilters() && (
                              <span className="bg-emerald-100 text-emerald-600 text-xs px-2 py-0.5 rounded-full">
                                Active
                              </span>
                            )}
                          </button>

                          <div className="text-sm text-gray-600">
                            {loading ? (
                              'Loading properties...'
                            ) : (
                              <>
                                <span className="font-medium">{filteredListings.length}</span> properties found
                                {hasActiveFilters() && (
                                  <span className="ml-2">
                                    ({allListings.filter(l => l.status === 'active').length} total)
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          {/* Clear Search Button */}
                          {(hasActiveFilters() || searchCriteria.location || searchCriteria.checkIn || searchCriteria.checkOut) && (
                            <button
                              onClick={clearAllSearch}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:text-red-700 font-medium border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              <X className="h-4 w-4" />
                              Clear Search
                            </button>
                          )}
                        </div>

                        {/* View Mode Toggle */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-lg transition-colors ${
                              viewMode === 'grid' 
                                ? 'bg-emerald-100 text-emerald-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            <Grid className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-lg transition-colors ${
                              viewMode === 'list' 
                                ? 'bg-emerald-100 text-emerald-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            <List className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

            {/* Results Grid/List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading properties...</p>
              </div>
            ) : filteredListings.length > 0 ? (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6'
                : 'space-y-4'
              }>
                {filteredListings.map((place, index) => (
                  viewMode === 'grid' ? (
                    <AccommodationPlaceCard
                      key={place.id || index}
                      place={place}
                      customStyling={customStyling}
                      searchCriteria={searchCriteria}
                      bookSettings={bookingSettings}
                    />
                  ) : (
                    <AccommodationPlaceRow
                      key={place.id || index}
                      place={place}
                      customStyling={customStyling}
                      searchCriteria={searchCriteria}
                      bookSettings={bookingSettings}
                    />
                  )
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No properties available
                </h3>
                <p className="text-gray-600">
                  Check back later for new listings
                </p>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

// Updated Search Places Form Component
const SearchPlacesForm = ({ 
  searchCriteria, 
  setSearchCriteria,
  filters,
  allListings,
  setFilters,
  onLocationChange, 
  onDateChange, 
  customStyling,
  availableLocations = []
}) => {
  return (
    <div className="space-y-2">
      {/* Date, Location and Guest Selection */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
        {/* Where To? (wide) */}
        <div className="lg:col-span-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Where To?</label>
          <LocationSelector
            allListings={allListings}
            selectedLocation={searchCriteria.location}
            onLocationChange={(province, city) => {
              onLocationChange(province, city);
              const location = city ? `${city}, ${province}` : province;
              setFilters(prev => ({ ...prev, location }));
            }}
          />
        </div>

        {/* Check In */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Check In</label>
          <div className="relative">
            <input
              type="date"
              value={searchCriteria.checkIn}
              onChange={(e) => onDateChange('checkIn', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="bg-gray-100 w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Check Out */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Check Out</label>
          <div className="relative">
            <input
              type="date"
              value={searchCriteria.checkOut}
              onChange={(e) => onDateChange('checkOut', e.target.value)}
              min={searchCriteria.checkIn || new Date().toISOString().split('T')[0]}
              className="bg-gray-100 w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Adults (narrow) */}
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Adults</label>
          <select
            value={searchCriteria.adults}
            onChange={(e) => setSearchCriteria(prev => ({ ...prev, adults: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {[1,2,3,4,5,6,7,8].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
        </div>

        {/* Children (narrow) */}
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Children</label>
          <select
            value={searchCriteria.children}
            onChange={(e) => setSearchCriteria(prev => ({ ...prev, children: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {[0,1,2,3,4,5,6].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
        </div>

        {/* Rooms (narrow) */}
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Rooms</label>
          <select
            value={searchCriteria.rooms}
            onChange={(e) => setSearchCriteria(prev => ({ ...prev, rooms: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {[1,2,3,4,5,6].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Location Selector Component
const LocationSelector = ({ onLocationChange, selectedLocation = '', allListings = [] }) => {
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  // Extract available locations from listings, fallback to SA_LOCATIONS
  const availableLocations = useMemo(() => {
    
    const locationMap = new Map();

    console.log('Sowing all listings', allListings);
    
    allListings.forEach(listing => {
      if (listing.status === 'active' && listing.location) {
        const location = listing.location.trim();
        
        let city = '';
        let province = '';
        
        if (location.includes(',')) {
          const parts = location.split(',').map(part => part.trim());
          if (parts.length >= 2) {
            city = parts[0];
            province = parts[1];
          } else {
            province = parts[0];
          }
        } else {
          province = location;
        }
        
        const key = province.toLowerCase();
        
        if (!locationMap.has(key)) {
          locationMap.set(key, {
            province: province,
            cities: new Set()
          });
        }
        
        if (city) {
          locationMap.get(key).cities.add(city);
        }
      }
    });
    
    const locations = {};
    locationMap.forEach((value, key) => {
      locations[value.province] = Array.from(value.cities).sort();
    });
    
    return locations;
  }, [allListings]);

  useEffect(() => {
    if (selectedLocation) {
      const [city, province] = selectedLocation.split(', ');
      if (province && Object.keys(availableLocations).includes(province)) {
        setSelectedProvince(province);
        setSelectedCity(city || '');
      }
    }
  }, [selectedLocation, availableLocations]);

  const handleProvinceChange = (province) => {
    setSelectedProvince(province);
    setSelectedCity('');
    onLocationChange(province, '');
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
    onLocationChange(selectedProvince, city);
  };

  const getAvailableCities = (province) => {
    return availableLocations[province] || [];
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        value={selectedProvince}
        onChange={(e) => handleProvinceChange(e.target.value)}
        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500"
      >
        <option value="">Select Province</option>
        {Object.keys(availableLocations).map(province => (
          <option key={province} value={province}>{province}</option>
        ))}
      </select>

      <select
        value={selectedCity}
        onChange={(e) => handleCityChange(e.target.value)}
        disabled={!selectedProvince}
        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100"
      >
        <option value="">Any City</option>
        {getAvailableCities(selectedProvince).map(city => (
          <option key={city} value={city}>{city}</option>
        ))}
      </select>
    </div>
  );
};

// Updated Accommodation Filters Component

const AccommodationFilters = ({ filters, setFilters, onClear }) => {
  const amenities = [
    { id: 'wifi', label: 'Free WiFi', icon: Wifi },
    { id: 'parking', label: 'Free Parking', icon: ParkingCircle },
    { id: 'breakfast', label: 'Breakfast', icon: Coffee },
    { id: 'restaurant', label: 'Restaurant', icon: UtensilsCrossed },
    { id: 'pool', label: 'Swimming Pool', icon: Building2 },
    { id: 'spa', label: 'Spa', icon: Building2 },
    { id: 'gym', label: 'Fitness Center', icon: Building2 },
    { id: 'petfriendly', label: 'Pet Friendly', icon: Heart }
  ];

  return (
    <div className="bg-gray-50 rounded-lg p-6 space-y-6 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Price Range (per night)</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">R</span>
            <input
              type="number"
              placeholder="Min"
              value={filters.priceRange[0] === 0 ? '' : filters.priceRange[0]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                priceRange: [parseInt(e.target.value) || 0, prev.priceRange[1]]
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
            <span className="text-gray-500">-</span>
            <span className="text-sm text-gray-500">R</span>
            <input
              type="number"
              placeholder="Max"
              value={filters.priceRange[1] === 10000 ? '' : filters.priceRange[1]}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                priceRange: [prev.priceRange[0], parseInt(e.target.value) || 10000]
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        {/* Accommodation Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Accommodation Type</label>
          <div className="space-y-2">
            {ACCOMMODATION_TYPES.slice(0, 4).map(type => (
              <label key={type.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.accommodationType.includes(type.value)}
                  onChange={(e) => {
                    const updated = e.target.checked
                      ? [...filters.accommodationType, type.value]
                      : filters.accommodationType.filter(t => t !== type.value);
                    setFilters(prev => ({ ...prev, accommodationType: updated }));
                  }}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-gray-700">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Star Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Star Rating</label>
          <div className="space-y-2">
            {[5,4,3,2,1].map(rating => (
              <label key={rating} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="starRating"
                  value={rating}
                  checked={filters.starRating === rating}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    starRating: parseInt(e.target.value) 
                  }))}
                  className="border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <div className="flex">
                  {[...Array(rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="text-sm text-gray-700">{rating}+ stars</span>
              </label>
            ))}
          </div>
        </div>

        {/* Guest Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Guest Rating</label>
          <div className="space-y-2">
            {[4.5, 4.0, 3.5, 3.0].map(rating => (
              <label key={rating} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="guestRating"
                  value={rating}
                  checked={filters.guestRating === rating}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    guestRating: parseFloat(e.target.value) 
                  }))}
                  className="border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-gray-700">{rating}+ rating</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Amenities */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Amenities</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {amenities.map(amenity => {
            const isSelected = filters.amenities.includes(amenity.id);
            const AmenityIcon = amenity.icon;
            
            return (
              <button
                key={amenity.id}
                onClick={() => {
                  const updated = isSelected
                    ? filters.amenities.filter(a => a !== amenity.id)
                    : [...filters.amenities, amenity.id];
                  setFilters(prev => ({ ...prev, amenities: updated }));
                }}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                  isSelected
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <AmenityIcon className="h-4 w-4" />
                {amenity.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Clear Filters */}
      <div className="flex justify-end">
        <button
          onClick={onClear}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
        >
          Clear All Filters
        </button>
      </div>
    </div>
  );
};

const SpecificPlaceForm = ({ searchCriteria, setSearchCriteria, customStyling, onSearch }) => {
  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchCriteria.specificPlace);
    }
  };

  return (
    <div className="max-w-2xl mx-auto text-center space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Looking for a specific place?</h3>
        <p className="text-gray-600">Enter the name or address of the accommodation you're looking for</p>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by property name, address, or landmark..."
          value={searchCriteria.specificPlace || ''}
          onChange={(e) => setSearchCriteria(prev => ({ ...prev, specificPlace: e.target.value }))}
          className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
      </div>

      <button
        onClick={handleSearch}
        className="px-8 py-3 rounded-lg font-medium text-white transition-colors"
        style={{ backgroundColor: customStyling?.primaryColor || '#10b981' }}
      >
        Search Specific Place
      </button>
    </div>
  );
};

export default BookingTemplate;